import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import GreaterThan from "./DrawerIcons/Chev1_right.svg";
import "./sidebar.css"; // Import your CSS file for styling
import {
  Clock,
  Users,
  LayoutDashboard,
  Briefcase,
  MapPin,
  MapPinned,
  Smartphone,
  CalendarClock,
  FileSpreadsheet,
  FileCog,
  ChevronRight,
  FileText,
  CreditCard,
  Building,
  UserSquare,
  ListChecks,
  Compass,
  Shield,
  ShieldCheck,
  FolderTree,
  History,
  Building2,
} from "lucide-react";
import { usePermission } from "../contexts/usePermission";

const Drawer = ({ response4, onItemClick }) => {
  const loginResponse = useSelector((state) => state.loginResponse);
  const userRole = loginResponse?.role_name || loginResponse?.roleName || '';
  // Get Super Admin selected organization from Redux
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const [activeMainIndex, setActiveMainIndex] = useState(null);
  const [activeSubIndex, setActiveSubIndex] = useState(null);
  const [submenuVisible, setSubmenuVisible] = useState(true); // Set to true by default
  const [label, setLabel] = useState("");
  const [expandedMenu, setExpandedMenu] = useState(null);
  const location = useLocation();

  // Get permission check functions
  const { hasPermission, isSuperAdmin, loading: permissionsLoading } = usePermission();

  useEffect(() => {
    const savedMainIndex = localStorage.getItem("activeMainIndex");
    const savedSubIndex = localStorage.getItem("activeSubIndex");
    const savedLabel = localStorage.getItem("label");

    const parsedMainIndex = parseInt(savedMainIndex);

    // If no valid saved state, default to Dashboard (index 0)
    if (isNaN(parsedMainIndex) || savedMainIndex === null) {
      setLabel("Dashboard");
      setActiveMainIndex(0);
      setActiveSubIndex(null);
      // Save the default state
      localStorage.setItem("activeMainIndex", "0");
      localStorage.setItem("label", "Dashboard");
      localStorage.removeItem("activeSubIndex");
    } else {
      setActiveMainIndex(parsedMainIndex);

      // Only set activeSubIndex if it's a valid number, otherwise keep it null
      if (savedSubIndex !== null && !isNaN(parseInt(savedSubIndex))) {
        const parsedSubIndex = parseInt(savedSubIndex);
        setActiveSubIndex(parsedSubIndex);
        // Auto-expand the menu if a submenu is active
        setExpandedMenu(parsedMainIndex);
      } else {
        setActiveSubIndex(null);
      }

      if (savedLabel !== null) {
        setLabel(savedLabel);
      }
    }
  }, []);

  useEffect(() => {
    if (activeMainIndex !== null) {
      localStorage.setItem("activeMainIndex", activeMainIndex);
    }
    if (activeSubIndex !== null) {
      localStorage.setItem("activeSubIndex", activeSubIndex);
    } else {
      // Remove from localStorage if activeSubIndex is null
      localStorage.removeItem("activeSubIndex");
    }
    if (label !== "") {
      localStorage.setItem("label", label);
    }
  }, [activeMainIndex, activeSubIndex, label]);

  const handleMainTabClick = (label, index, hasSubmenus) => {
    if (hasSubmenus) {
      setExpandedMenu(expandedMenu === index ? null : index);
    } else {
      setExpandedMenu(null);
      // Reset submenu selection when clicking non-submenu items
      setActiveSubIndex(null);
    }
    setActiveMainIndex(index);
    setLabel(label);
  };

  const handleSubTabClick = (mainIndex, subIndex, subLabel) => {
    setActiveMainIndex(mainIndex);
    setActiveSubIndex(subIndex);
    setLabel(subLabel); // Update label to submenu label
  };

  // Menu items with permission codes
  const menuItems = [
    {
      icon: <LayoutDashboard size={16} className="home-icon" />,
      label: "Dashboard",
      url: "",
      permissionCode: "MENU_DASHBOARD",
    },
    {
      icon: <LayoutDashboard size={16} className="home-icon" />,
      label: "Analytics",
      url: "analytics",
      permissionCode: "MENU_ANALYTICS",
    },
    {
      icon: <Clock size={16} className="home-icon" />,
      label: "Timesheets",
      url: "timesheets",
      permissionCode: "MENU_TIMESHEETS",
    },
    {
      icon: <Users size={16} className="home-icon" />,
      label: "Users",
      url: "members",
      permissionCode: "MENU_USERS",
    },
    {
      icon: <Briefcase size={16} className="home-icon" />,
      label: "Work Schedule",
      url: "addworkschedule",
      permissionCode: "MENU_WORK_SCHEDULE",
    },
    {
      icon: <MapPin size={16} className="home-icon" />,
      label: "Locations",
      url: "addWorkLocation",
      permissionCode: "MENU_LOCATIONS",
    },
    {
      icon: <MapPinned size={16} className="home-icon" />,
      label: "Tracking",
      url: "locationTracking",
      permissionCode: "MENU_TRACKING",
    },
    {
      icon: <Smartphone size={16} className="home-icon" />,
      label: "Mobile App Usage",
      url: "appusage",
      permissionCode: "MENU_MOBILE_APP_USAGE",
    },
    {
      icon: <FileCog size={16} className="home-icon" />,
      label: "Mobile App Setup",
      url: "monitoring",
      permissionCode: "MENU_MOBILE_APP_SETUP",
    },
    {
      icon: <CalendarClock size={16} className="home-icon" />,
      label: "Work Assignment",
      url: "workassignments",
      permissionCode: "MENU_WORK_ASSIGNMENT",
    },
    {
      icon: <FileSpreadsheet size={16} className="home-icon" />,
      label: "Reports",
      url: "reports",
      permissionCode: "MENU_REPORTS",
    },
    {
      icon: <History size={16} className="home-icon" />,
      label: "Audit Logs",
      url: "audit-logs",
      // No permissionCode - visible for all roles
    },
    {
      icon: <Building size={17} className="home-icon" />,
      label: "Organization ",
      url: "accountsetting",
      permissionCode: "MENU_ORGANIZATION",
      submenus: [
        {
          icon: <UserSquare size={14} className="submenu-icon" />,
          label: "Organization Profile",
          url: "accountsetting",
          permissionCode: "SCREEN_SETTINGS_VIEW",
        },
        {
          icon: <FolderTree size={14} className="submenu-icon" />,
          label: "Sub-Organizations",
          url: "sub-organizations",
          permissionCode: "SCREEN_SUB_ORG_MANAGE",
        },
        {
          icon: <Building2 size={14} className="submenu-icon" />,
          label: "Child Organizations",
          url: "child-organizations",
          permissionCode: "FEATURE_ORGANIZATION_MANAGE_ROLES",
        },
      ],
    },
  ];

  // Super Admin menu items (only for SUPER_ADMIN role) - each as separate main menu
  const superAdminMenuItems = [
    {
      icon: <LayoutDashboard size={16} className="home-icon" />,
      label: "Dashboard",
      url: "/home/super-admin",
      permissionCode: "SCREEN_SUPER_ADMIN_DASHBOARD",
    },
    {
      icon: <Building size={16} className="home-icon" />,
      label: "Organizations",
      url: "/home/super-admin/organizations",
      permissionCode: "SCREEN_SUPER_ADMIN_ORGS",
    },
    {
      icon: <CreditCard size={16} className="home-icon" />,
      label: "Plans",
      url: "/home/super-admin/plans",
      permissionCode: "SCREEN_SUPER_ADMIN_PLANS",
    },
    {
      icon: <FileText size={16} className="home-icon" />,
      label: "Audit Logs",
      url: "/home/super-admin/audit-logs",
      permissionCode: "SCREEN_SUPER_ADMIN_AUDIT",
    },
    {
      icon: <Shield size={16} className="home-icon" />,
      label: "Admin Users",
      url: "/home/super-admin/users",
      permissionCode: "SCREEN_SUPER_ADMIN_USERS",
    },
    {
      icon: <ShieldCheck size={16} className="home-icon" />,
      label: "Role Permission",
      url: "/home/super-admin/role-permissions",
      permissionCode: "SCREEN_SUPER_ADMIN_ROLE_PERMISSIONS",
    },
  ];

  // Function to check if a menu item should be visible
  const canAccessMenuItem = (item) => {
    // Super admin can access everything
    if (isSuperAdmin) return true;

    // If no permission code, show by default
    if (!item.permissionCode) return true;

    // Check permission
    return hasPermission(item.permissionCode);
  };

  // Filter menu items based on permissions and Super Admin org selection
  const filteredMenuItems = useMemo(() => {
    // For Super Admin role
    if (userRole === 'SUPER_ADMIN') {
      // If no organization is selected, show only Super Admin menu
      if (!superAdminSelectedOrgId) {
        return superAdminMenuItems
          .filter(canAccessMenuItem)
          .map(item => {
            if (item.submenus) {
              const filteredSubmenus = item.submenus.filter(canAccessMenuItem);
              if (filteredSubmenus.length === 0) return null;
              return { ...item, submenus: filteredSubmenus };
            }
            return item;
          })
          .filter(Boolean);
      }

      // If organization is selected, show Super Admin menu + Organization Data menu with org-related items as submenus
      const orgName = superAdminSelectedOrgData?.org_name || 'Organization';

      // Convert organization-related menu items to submenus
      const orgSubmenus = menuItems
        .filter(item => item.label !== 'Organization ') // Exclude the Organization settings menu
        .filter(canAccessMenuItem)
        .map(item => ({
          icon: item.icon,
          label: item.label,
          url: item.url,
          permissionCode: item.permissionCode,
        }));

      // Get the Organization settings submenus (Organization Profile,
      // Sub-Organizations, Child Organizations). The sidebar only renders a
      // single level of submenus, so these must be spread directly into the
      // org data menu — nesting them under one "Organization Settings" entry
      // would silently drop them.
      const orgSettingsItem = menuItems.find(item => item.label === 'Organization ');
      const orgSettingsSubmenus = (orgSettingsItem?.submenus || [])
        .filter(canAccessMenuItem)
        .map(submenu => ({
          icon: submenu.icon,
          label: submenu.label,
          url: submenu.url,
          permissionCode: submenu.permissionCode,
        }));

      // Create the Organization Data main menu with all org-related items as submenus
      const orgDataMenu = {
        icon: <Building size={17} className="home-icon" />,
        label: orgName,
        url: "",
        permissionCode: "MENU_ORG_DATA",
        submenus: [
          ...orgSubmenus,
          // Org settings items (profile, sub-orgs, child-orgs) as direct submenus
          ...orgSettingsSubmenus,
        ],
      };

      // Filter Super Admin menu items
      const filteredSuperAdminItems = superAdminMenuItems
        .filter(canAccessMenuItem)
        .map(item => {
          if (item.submenus) {
            const filteredSubmenus = item.submenus.filter(canAccessMenuItem);
            if (filteredSubmenus.length === 0) return null;
            return { ...item, submenus: filteredSubmenus };
          }
          return item;
        })
        .filter(Boolean);

      return [...filteredSuperAdminItems, orgDataMenu];
    }

    // For regular users, show normal menu items
    return menuItems
      .filter(canAccessMenuItem)
      .map(item => {
        // If item has submenus, filter those too
        if (item.submenus) {
          const filteredSubmenus = item.submenus.filter(canAccessMenuItem);
          // If no submenus are accessible, hide the parent too
          if (filteredSubmenus.length === 0) return null;
          return { ...item, submenus: filteredSubmenus };
        }
        return item;
      })
      .filter(Boolean); // Remove null items
  }, [userRole, isSuperAdmin, hasPermission, permissionsLoading, superAdminSelectedOrgId, superAdminSelectedOrgData]);

  // Use filtered menu items instead of allMenuItems
  const allMenuItems = filteredMenuItems;

  // Sync sidebar highlighting with current URL when navigating via browser URL change
  useEffect(() => {
    // Get the current path without /home prefix (for matching relative URLs)
    const currentPath = location.pathname.replace('/home/', '').replace('/home', '');
    // Also keep the full pathname for matching absolute URLs
    const fullPath = location.pathname;

    const urlMatches = (itemUrl) => {
      if (!itemUrl || itemUrl === '#') return false;
      // Match absolute URL (e.g. /home/super-admin)
      if (itemUrl.startsWith('/')) return itemUrl === fullPath;
      // Match relative URL (e.g. analytics)
      return itemUrl === currentPath || (currentPath === '' && itemUrl === '') || (currentPath === '/' && itemUrl === '');
    };

    // Find matching menu item based on current URL
    let foundMainIndex = null;
    let foundSubIndex = null;
    let foundLabel = null;

    allMenuItems.forEach((item, mainIndex) => {
      // Check if main item URL matches
      if (urlMatches(item.url)) {
        foundMainIndex = mainIndex;
        foundSubIndex = null;
        foundLabel = item.label;
      }

      // Check submenus if they exist
      if (item.submenus) {
        item.submenus.forEach((submenu, subIndex) => {
          if (urlMatches(submenu.url)) {
            foundMainIndex = mainIndex;
            foundSubIndex = subIndex;
            foundLabel = submenu.label;
            // Auto-expand the parent menu
            setExpandedMenu(mainIndex);
          }
        });
      }
    });

    // Update state if a match was found
    if (foundMainIndex !== null) {
      setActiveMainIndex(foundMainIndex);
      setActiveSubIndex(foundSubIndex);
      if (foundLabel) {
        setLabel(foundLabel);
      }
    }
  }, [location.pathname, allMenuItems]);

  // Check if an item is the organization menu (has MENU_ORG_DATA permission code)
  const isOrgMenu = (item) => item.permissionCode === "MENU_ORG_DATA";

  // Get the index where org menu starts (for adding divider)
  const orgMenuIndex = allMenuItems.findIndex(isOrgMenu);

  return (
    <div className="drawer p-0 m-0 bg-color">
      <div className="row m-0 g-0">
        {allMenuItems.map((item, mainIndex) => (
          <div key={mainIndex} className="tab-container">
            {/* Add divider before organization menu */}
            {mainIndex === orgMenuIndex && orgMenuIndex > 0 && (
              <div className="menu-section-divider" />
            )}

            <Link
              to={item.submenus ? "#" : item.url}
              className={`left-nav-tab ${
                activeMainIndex === mainIndex && activeSubIndex === null ? "active" : ""
              } ${isOrgMenu(item) ? "org-menu-header" : ""}`}
              onClick={() => handleMainTabClick(item.label, mainIndex, !!item.submenus)}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {item.icon}
                  <span title={item.label}>{item.label}</span>
                </div>
                {item.submenus && (
                  <ChevronRight
                    size={16}
                    className={`chevron-icon ${expandedMenu === mainIndex ? 'rotated' : ''}`}
                  />
                )}
              </div>
            </Link>
            {item.submenus &&
              expandedMenu === mainIndex && (
                <div className={isOrgMenu(item) ? "org-submenu-wrapper" : "submenu-wrapper"}>
                  <div className={`${isOrgMenu(item) ? "org-submenu-scroll" : "submenu"} ${activeMainIndex === mainIndex ? 'active' : ''}`}>
                    {item.submenus.map((submenu, subIndex) => (
                      <Link
                        key={subIndex}
                        to={submenu.url}
                        className={`${isOrgMenu(item) ? "org-submenu-item" : "submenu-item"} ${
                          activeMainIndex === mainIndex && activeSubIndex === subIndex && activeSubIndex !== null ? "active" : ""
                        }`}
                      >
                        {submenu.icon}
                        <span>{submenu.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Drawer;
